import { useState } from 'react';
import api from '../services/api';

export default function BookerProfileForm({ initialData = null, onSuccess, mode = 'create' }) {
  const [formData, setFormData] = useState({
    companyName: initialData?.companyName || '',
    bio: initialData?.bio || '',
    city: initialData?.city || '',
    country: initialData?.country || 'France',
    phone: initialData?.phone || '',
    website: initialData?.website || '',
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState(null);

  const validateForm = () => {
    const newErrors = {};

    // companyName validation
    if (!formData.companyName.trim()) {
      newErrors.companyName = 'Le nom de l\'entreprise est requis';
    } else if (formData.companyName.length > 150) {
      newErrors.companyName = 'Le nom doit faire 150 caractères maximum';
    }

    // bio validation
    if (formData.bio && formData.bio.length > 2000) {
      newErrors.bio = 'La bio doit faire 2000 caractères maximum';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError(null);

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const payload = {
        companyName: formData.companyName.trim(),
        bio: formData.bio.trim(),
        city: formData.city.trim(),
        country: formData.country.trim(),
        phone: formData.phone.trim(),
        website: formData.website.trim(),
      };

      if (mode === 'create') {
        await api.post('/bookers/profile', payload);
      } else {
        await api.put('/bookers/profile', payload);
      }

      onSuccess?.();
    } catch (err) {
      setApiError(err.message || 'Erreur lors de la sauvegarde. Veuillez réessayer.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="border th-border p-6 space-y-6 th-bg-surface transition-colors duration-300"
    >
      {/* API Error */}
      {apiError && (
        <div className="border border-status-rejected/30 text-status-rejected px-3 py-2 text-sm font-mono">
          {apiError}
        </div>
      )}

      {/* Company Name */}
      <div>
        <label htmlFor="companyName" className="block text-xs font-mono th-text-secondary mb-2 uppercase tracking-widest">
          Nom de l'agence <span className="text-accent">*</span>
        </label>
        <input
          id="companyName"
          type="text"
          name="companyName"
          value={formData.companyName}
          onChange={handleChange}
          maxLength={150}
          placeholder="Nom de votre agence ou structure"
          className="th-input"
        />
        {errors.companyName && <div className="text-status-rejected text-xs mt-1 font-mono">{errors.companyName}</div>}
        <div className="th-text-muted text-xs mt-1 font-mono">
          {formData.companyName.length}/150
        </div>
      </div>

      {/* Bio */}
      <div>
        <label htmlFor="bio" className="block text-xs font-mono th-text-secondary mb-2 uppercase tracking-widest">
          Bio
        </label>
        <textarea
          id="bio"
          name="bio"
          value={formData.bio}
          onChange={handleChange}
          maxLength={2000}
          placeholder="Décrivez votre agence (optionnel)"
          rows="4"
          className="th-input resize-none"
        />
        {errors.bio && <div className="text-status-rejected text-xs mt-1 font-mono">{errors.bio}</div>}
        <div className="th-text-muted text-xs mt-1 font-mono">
          {formData.bio.length}/2000
        </div>
      </div>

      {/* City */}
      <div>
        <label htmlFor="city" className="block text-xs font-mono th-text-secondary mb-2 uppercase tracking-widest">
          Ville
        </label>
        <input
          id="city"
          type="text"
          name="city"
          value={formData.city}
          onChange={handleChange}
          placeholder="Votre ville (optionnel)"
          className="th-input"
        />
      </div>

      {/* Country */}
      <div>
        <label htmlFor="country" className="block text-xs font-mono th-text-secondary mb-2 uppercase tracking-widest">
          Pays
        </label>
        <input
          id="country"
          type="text"
          name="country"
          value={formData.country}
          onChange={handleChange}
          placeholder="Votre pays"
          className="th-input"
        />
      </div>

      {/* Phone */}
      <div>
        <label htmlFor="phone" className="block text-xs font-mono th-text-secondary mb-2 uppercase tracking-widest">
          Téléphone
        </label>
        <input
          id="phone"
          type="tel"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          placeholder="Votre numéro (optionnel)"
          className="th-input"
        />
      </div>

      {/* Website */}
      <div>
        <label htmlFor="website" className="block text-xs font-mono th-text-secondary mb-2 uppercase tracking-widest">
          Site web
        </label>
        <input
          id="website"
          type="url"
          name="website"
          value={formData.website}
          onChange={handleChange}
          placeholder="https://example.com (optionnel)"
          className="th-input"
        />
      </div>

      {/* Submit Button */}
      <button
        type="submit"
        disabled={isLoading}
        className="w-full th-btn-accent disabled:opacity-50"
      >
        {isLoading ? 'Enregistrement...' : mode === 'create' ? 'Créer le profil' : 'Enregistrer'}
      </button>
    </form>
  );
}
